"use client";

import Image from "next/image";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useEffect, useRef } from "react";
import { useFocusTrap } from "@/lib/useFocusTrap";

export type LightboxImage = {
  url: string;
  altText?: string | null;
  caption?: string | null;
};

export function GalleryLightbox({
  images,
  activeIndex,
  onChange,
  onClose,
}: {
  images: LightboxImage[];
  activeIndex: number | null;
  onChange: (index: number) => void;
  onClose: () => void;
}) {
  const open = activeIndex !== null && Boolean(images[activeIndex]);
  const dialogRef = useFocusTrap<HTMLDivElement>(open);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const image = activeIndex === null ? null : images[activeIndex];

  useEffect(() => {
    if (!open) return;
    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
      previousFocusRef.current?.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open || activeIndex === null) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft" && images.length > 1) onChange((activeIndex - 1 + images.length) % images.length);
      if (event.key === "ArrowRight" && images.length > 1) onChange((activeIndex + 1) % images.length);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [activeIndex, images.length, onChange, onClose, open]);

  if (!open || !image || activeIndex === null) return null;

  const previous = () => onChange((activeIndex - 1 + images.length) % images.length);
  const next = () => onChange((activeIndex + 1) % images.length);

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={image.caption || image.altText || "Gallery image"}
      tabIndex={-1}
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/90 p-4"
    >
      <div className="relative flex max-h-full w-full max-w-7xl flex-1 items-center justify-center">
        <Image
          src={image.url}
          alt={image.altText || ""}
          width={1800}
          height={1200}
          className="max-h-[calc(100vh-8rem)] max-w-[calc(100vw-2rem)] object-contain"
          sizes="100vw"
          priority
        />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close image viewer"
          title="Close"
          className="absolute right-0 top-0 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-900 shadow-lg hover:bg-slate-100"
        >
          <X className="h-5 w-5" aria-hidden />
        </button>
        {images.length > 1 && (
          <>
            <button
              type="button"
              onClick={previous}
              aria-label="Previous image"
              title="Previous image"
              className="absolute left-0 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-900 shadow-lg hover:bg-slate-100"
            >
              <ChevronLeft className="h-6 w-6" aria-hidden />
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Next image"
              title="Next image"
              className="absolute right-0 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-900 shadow-lg hover:bg-slate-100"
            >
              <ChevronRight className="h-6 w-6" aria-hidden />
            </button>
          </>
        )}
      </div>
      <div className="mt-3 min-h-10 text-center text-sm text-white">
        {image.caption && <p>{image.caption}</p>}
        <p className="mt-1 text-xs text-white/70">{activeIndex + 1} of {images.length}</p>
      </div>
    </div>
  );
}
